// Developer: Ankit Gupta
import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Spin } from 'antd';
import { useT } from '@/i18n';

import { MainLayout } from '@/layouts/MainLayout';
import { ProjectLayout } from '@/layouts/ProjectLayout';
import { ProtectedRoute } from '@/auth/AuthProvider';
import { DevProtectedRoute } from '@/auth/DevAuthProvider';

const isDevAuth = import.meta.env.VITE_DEV_AUTH === 'true';
const Guard = isDevAuth ? DevProtectedRoute : ProtectedRoute;

// ─── Lazy-loaded pages ────────────────────────────────────────────────────────

const Dashboard           = lazy(() => import('@/pages/Dashboard'));
const ProjectList         = lazy(() => import('@/pages/Projects/ProjectList'));
const ProjectDetail       = lazy(() => import('@/pages/Projects/ProjectDetail'));
const ProjectSettings     = lazy(() => import('@/pages/Projects/ProjectSettings'));
const ProjectMembers      = lazy(() => import('@/pages/Projects/ProjectMembers'));
const ProjectEnvironments = lazy(() => import('@/pages/Projects/ProjectEnvironments'));
const ProjectEntityLibraries = lazy(() => import('@/pages/Projects/ProjectEntityLibraries'));
const ProjectTemplates = lazy(() => import('@/pages/Projects/ProjectTemplates'));
const ProjectFusionExtracts = lazy(() => import('@/pages/Projects/ProjectFusionExtracts'));
const ProjectSourceMapping = lazy(() => import('@/pages/Projects/SourceMapping'));
const ProjectApiSources   = lazy(() => import('@/pages/Projects/ProjectApiSources'));
const ProjectCadInbox     = lazy(() => import('@/pages/Projects/ProjectCadInbox'));
const ProjectRuns         = lazy(() => import('@/pages/Projects/ProjectRuns'));
const EntityLibrary       = lazy(() => import('@/pages/EntityLibrary'));
const EntityGroupDetail   = lazy(() => import('@/pages/EntityLibrary/EntityGroupDetail'));
const EntityDetail        = lazy(() => import('@/pages/EntityLibrary/EntityDetail'));
const LibraryTemplates    = lazy(() => import('@/pages/EntityLibrary/LibraryTemplates'));
const SubmitRun           = lazy(() => import('@/pages/Runs/SubmitRun'));
const FusionSetupExport   = lazy(() => import('@/pages/FusionSetupExport'));
const RunHistory          = lazy(() => import('@/pages/Runs/RunHistory'));
const RunDetail           = lazy(() => import('@/pages/Runs/RunDetail'));
const RunLineDetail       = lazy(() => import('@/pages/Runs/RunLineDetail'));
const IssueList           = lazy(() => import('@/pages/Issues/IssueList'));
const UserManagement      = lazy(() => import('@/pages/Admin/UserManagement'));
const AdminConfig         = lazy(() => import('@/pages/Admin/AdminConfig'));
const MailTemplates       = lazy(() => import('@/pages/Admin/MailTemplates'));
const KeyStore            = lazy(() => import('@/pages/Admin/KeyStore'));
const Lookups             = lazy(() => import('@/pages/Admin/Lookups'));
const Maintenance         = lazy(() => import('@/pages/Admin/Maintenance'));
const MyFeedback          = lazy(() => import('@/pages/Feedback/MyFeedback'));
const MyTimesheet         = lazy(() => import('@/pages/Timesheet'));
const ManageFeedback      = lazy(() => import('@/pages/Feedback/Manage'));
const ProductInsights     = lazy(() => import('@/pages/Feedback/Insights'));
const Debug               = lazy(() => import('@/pages/Admin/Debug'));
const ExtractValidationMapping = lazy(() => import('@/pages/Admin/ExtractValidationMapping'));
const AdminAnalytics      = lazy(() => import('@/pages/Admin/Analytics'));
const Reports             = lazy(() => import('@/pages/Reports'));
const FusionExtracts      = lazy(() => import('@/pages/FusionExtracts'));
const Reconciliation      = lazy(() => import('@/pages/Reconciliation'));
const ScheduledRuns       = lazy(() => import('@/pages/ScheduledRuns'));
const Rhino               = lazy(() => import('@/pages/Rhino'));
const DataSync            = lazy(() => import('@/pages/DataSync'));
const Credits             = lazy(() => import('@/pages/Credits'));
const CreditsAdmin        = lazy(() => import('@/pages/Admin/CreditsAdmin'));
const Earnings            = lazy(() => import('@/pages/Admin/Earnings'));
const Profile             = lazy(() => import('@/pages/Profile'));
const InsightShell        = lazy(() => import('@/pages/Insight/InsightShell'));
const InsightOverview     = lazy(() => import('@/pages/Insight/Overview'));
const InsightWorkspace    = lazy(() => import('@/pages/Insight/Workspace'));
const InsightDataSources  = lazy(() => import('@/pages/Insight/DataSources'));
const InsightTeams        = lazy(() => import('@/pages/Insight/Teams'));
const InsightUsers        = lazy(() => import('@/pages/Insight/Users'));
const InsightFiles        = lazy(() => import('@/pages/Insight/Files'));
const InsightBrandingKit  = lazy(() => import('@/pages/Insight/BrandingKit'));
const InsightSettings     = lazy(() => import('@/pages/Insight/Settings'));
const InsightSubscription = lazy(() => import('@/pages/Insight/Subscription'));
const InsightHelp         = lazy(() => import('@/pages/Insight/Help'));
const InsightKnowledgeGraph = lazy(() => import('@/pages/Insight/KnowledgeGraph'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const InsightDashboardView  = lazy(() => import('@/pages/Insight/DashboardView'));
const InsightSpecDashboard  = lazy(() => import('@/pages/Insight/SpecDashboardView'));
const InsightDocuments      = lazy(() => import('@/pages/Insight/Documents/DocumentsPage'));
const InsightCatalogue      = lazy(() => import('@/pages/Insight/Catalogue/CataloguePage'));
const InsightMetricDetail   = lazy(() => import('@/pages/Insight/Catalogue/MetricDetailPage'));
const InsightAudit          = lazy(() => import('@/pages/Insight/Admin/AuditPage'));
const InsightHealth         = lazy(() => import('@/pages/Insight/Admin/HealthPage'));
const InsightAccess         = lazy(() => import('@/pages/Insight/Admin/AccessPage'));
const InsightTenants        = lazy(() => import('@/pages/Insight/Tenants/TenantsPage'));
const InsightWorkbooks      = lazy(() => import('@/pages/Insight/Workbooks/WorkbooksPage'));
const ConfyShell            = lazy(() => import('@/pages/Confy/ConfyShell'));
const ConfyOverview         = lazy(() => import('@/pages/Confy/Overview'));
const ConfyCatalog          = lazy(() => import('@/pages/Confy/Catalog'));
const ConfyWorkbooks        = lazy(() => import('@/pages/Confy/Workbooks'));
const ConfyRuns             = lazy(() => import('@/pages/Confy/Runs'));
const ConfyRunDetail        = lazy(() => import('@/pages/Confy/RunDetail'));
const ConfyCompare          = lazy(() => import('@/pages/Confy/Compare'));
const ConfyDrift            = lazy(() => import('@/pages/Confy/Drift'));
const ConfySmoke            = lazy(() => import('@/pages/Confy/Smoke'));
const ConfyReadiness        = lazy(() => import('@/pages/Confy/Readiness'));
const ConfyAssistant        = lazy(() => import('@/pages/Confy/Assistant'));
const ConfyEnvironment      = lazy(() => import('@/pages/Confy/Environment'));
const ConfyMappings         = lazy(() => import('@/pages/Confy/Mappings'));
const ConfyHelp             = lazy(() => import('@/pages/Confy/Help'));
const ConfyPlanPage         = lazy(() => import('@/pages/Confy/Plan'));
const ConfyDesigner         = lazy(() => import('@/pages/Confy/Designer'));
const ConfyBaselines        = lazy(() => import('@/pages/Confy/Baselines'));
const ChatPage              = lazy(() => import('@/pages/Chat'));
const OrionApplications     = lazy(() => import('@/pages/Chat/Applications'));
const OrionApprovals        = lazy(() => import('@/pages/Chat/Approvals'));
const OrionAudit            = lazy(() => import('@/pages/Chat/Audit'));
const OrionPatches          = lazy(() => import('@/pages/Chat/Patches'));
const OrionOperations       = lazy(() => import('@/pages/Chat/Operations'));
const CompanyPage           = lazy(() => import('@/pages/Company'));
const JoinPage              = lazy(() => import('@/pages/Company/Join'));
const OrganizationDirectory = lazy(() => import('@/pages/Company/Directory'));
const OrganizationGate      = lazy(() => import('@/pages/Company/OrganizationGate'));
const Landing               = lazy(() => import('@/pages/Landing'));
const TermsPage             = lazy(() => import('@/pages/Legal/Terms'));

// ─── Unauthorised ─────────────────────────────────────────────────────────────

function Unauthorized() {
  const { t } = useT('shell');
  return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <h2>{t('unauthorized.title')}</h2>
      <p>{t('unauthorized.body')}</p>
    </div>
  );
}

// ─── Loading fallback ─────────────────────────────────────────────────────────

const PageLoader = () => (
  <div style={{ height: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Spin size="large" />
  </div>
);

// ─── Route tree ───────────────────────────────────────────────────────────────

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* The front door: what DataPilot is, the plans, sign in or create a company. Public. */}
        <Route path="/welcome" element={<Landing />} />
        <Route path="/terms" element={<TermsPage />} />

        {/* An invitation link: signed in, but before the organization gate, because the
            person it is for has no organization yet. */}
        <Route path="/join" element={<Guard><JoinPage /></Guard>} />

        {/* Insight - a standalone app shell with its own menu, not MainLayout */}
        <Route
          path="/insight"
          element={
            <Guard requiredPermission="insight.use"><OrganizationGate><InsightShell /></OrganizationGate></Guard>
          }
        >
          <Route index element={<InsightOverview />} />
          <Route path="workspace" element={<InsightWorkspace />} />
          <Route path="workspace/organization" element={<InsightWorkspace />} />
          <Route path="dashboards/:dashboardId" element={<InsightDashboardView />} />
          {/* Specification dashboards: the agent writes the spec, the frontend
              renders it. Separate from the tile dashboards above until the v2
              semantic API replaces them in sprint 5. */}
          <Route path="spec" element={<InsightSpecDashboard />} />
          <Route path="spec/:specId" element={<InsightSpecDashboard />} />
          <Route path="documents" element={<InsightDocuments />} />
          <Route path="catalogue" element={<InsightCatalogue />} />
          <Route path="catalogue/:key" element={<InsightMetricDetail />} />
          <Route path="admin/audit" element={<InsightAudit />} />
          <Route path="admin/health" element={<InsightHealth />} />
          <Route path="admin/access" element={<InsightAccess />} />
          <Route path="tenants" element={<InsightTenants />} />
          <Route path="workbooks" element={<InsightWorkbooks />} />
          <Route path="teams" element={<InsightTeams />} />
          <Route path="users" element={<InsightUsers />} />
          <Route path="data-sources" element={<InsightDataSources />} />
          <Route
            path="data-sources/:environmentId/knowledge-graph"
            element={<InsightKnowledgeGraph />}
          />
          <Route path="files" element={<InsightFiles />} />
          <Route path="branding" element={<InsightBrandingKit />} />
          <Route path="settings" element={<InsightSettings />} />
          <Route path="subscription" element={<InsightSubscription />} />
          <Route path="help" element={<InsightHelp />} />
          <Route path="*" element={<NotFound home="/insight" />} />
        </Route>

        {/* AAFAQ Confy - Oracle Fusion configuration automation; its own shell, like Insight */}
        <Route
          path="/confy"
          element={
            <Guard requiredPermission="confy.use"><OrganizationGate><ConfyShell /></OrganizationGate></Guard>
          }
        >
          <Route index element={<ConfyOverview />} />
          <Route path="catalog" element={<ConfyCatalog />} />
          <Route path="plan" element={<ConfyPlanPage />} />
          <Route path="designer" element={<ConfyDesigner />} />
          <Route path="baselines" element={<ConfyBaselines />} />
          <Route path="workbooks" element={<ConfyWorkbooks />} />
          <Route path="runs" element={<ConfyRuns />} />
          <Route path="runs/:runId" element={<ConfyRunDetail />} />
          <Route path="compare" element={<ConfyCompare />} />
          <Route path="drift" element={<ConfyDrift />} />
          <Route path="smoke" element={<ConfySmoke />} />
          <Route path="readiness" element={<ConfyReadiness />} />
          <Route path="assistant" element={<ConfyAssistant />} />
          <Route path="environment" element={<ConfyEnvironment />} />
          <Route path="mappings" element={<ConfyMappings />} />
          <Route path="help" element={<ConfyHelp />} />
          <Route path="*" element={<NotFound home="/confy" />} />
        </Route>

        <Route element={<Guard><OrganizationGate><MainLayout /></OrganizationGate></Guard>}>
          {/* Dashboard */}
          <Route index element={<Dashboard />} />

          <Route path="data-sync" element={<Guard requiredPermission="data.sync"><DataSync /></Guard>} />

          {/* Projects */}
          <Route path="projects" element={<ProjectList />} />
          <Route path="projects/:id" element={<ProjectLayout />}>
            <Route index element={<ProjectDetail />} />
            <Route path="settings" element={<ProjectSettings />} />
            <Route path="members" element={<ProjectMembers />} />
            <Route path="environments" element={<ProjectEnvironments />} />
            <Route path="entity-libraries" element={<ProjectEntityLibraries />} />
            <Route path="templates" element={<ProjectTemplates />} />
            {/* Legacy paths now consolidated under Entity Libraries */}
            <Route path="entities" element={<Navigate to="../entity-libraries" replace />} />
            <Route path="validations" element={<Navigate to="../entity-libraries" replace />} />
            <Route path="transforms" element={<Navigate to="../entity-libraries" replace />} />
            <Route path="fusion-extracts" element={<ProjectFusionExtracts />} />
            <Route path="source-mapping" element={<ProjectSourceMapping />} />
            <Route path="api-sources" element={<ProjectApiSources />} />
            <Route path="cad-inbox" element={<ProjectCadInbox />} />
            <Route path="runs" element={<ProjectRuns />} />
          </Route>
          {/* Run detail route - outside ProjectLayout so the breadcrumb works */}
          <Route path="projects/:projectId/runs/:runId" element={<RunDetail />} />
          <Route path="projects/:projectId/runs/:runId/lines/:lineId" element={<RunLineDetail />} />

          {/* Entity Library */}
          <Route path="entity-library">
            <Route index element={<EntityLibrary />} />
            <Route path="templates" element={<LibraryTemplates />} />
            <Route path=":groupId" element={<EntityGroupDetail />} />
            <Route path=":groupId/:entityId" element={<EntityDetail />} />
          </Route>

          {/* Migrate Data / Runs */}
          <Route path="runs" element={<Guard requiredPermission="runs.use"><Outlet /></Guard>}>
            <Route index element={<RunHistory />} />
            <Route path="submit" element={<SubmitRun />} />
            <Route path="fusion-export" element={<FusionSetupExport />} />
          </Route>

          {/* Issues */}
          <Route path="issues">
            <Route index element={<IssueList />} />
            <Route path="new" element={<IssueList />} />
            <Route path="mine" element={<IssueList />} />
          </Route>

          {/* Admin - Guard wraps an Outlet so nested routes render.
              NOT /admin: that path is proxied to Keycloak before it reaches
              this app, so every route under it returned Keycloak's own error
              page instead of ours. /insight/admin/* was unaffected because it
              is namespaced, which is the same reason this is /manage. */}
          <Route
            path="manage"
            element={
              <Guard requiredPermission="platform.admin">
                <Outlet />
              </Guard>
            }
          >
            <Route index element={<Navigate to="/manage/users" replace />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="config" element={<AdminConfig />} />
            <Route path="keystore" element={<KeyStore />} />
            <Route path="lookups" element={<Lookups />} />
            <Route path="maintenance" element={<Maintenance />} />
            <Route path="debug" element={<Debug />} />
            <Route path="mail-templates" element={<MailTemplates />} />
            <Route path="extract-validations" element={<ExtractValidationMapping />} />
            <Route path="subscriptions" element={<CreditsAdmin />} />
            <Route path="earnings" element={<Earnings />} />
            <Route path="organizations" element={<OrganizationDirectory />} />
          </Route>

          {/* Credits: any signed-in user can see their own balance. */}
          <Route path="credits" element={<Credits />} />
          <Route path="profile" element={<Profile />} />
          {/* The company: members, invitations, join requests. Every member may look. */}
          <Route path="company" element={<CompanyPage />} />

          {/* My timesheet: the page itself says so when it is not open for the person */}
          <Route path="timesheet" element={<MyTimesheet />} />
          {/* Feedback: everyone sees their own; a company's administrators and the DataPilot team triage it and read Product Insights */}
          <Route path="feedback" element={<MyFeedback />} />
          <Route path="feedback/manage" element={<Guard requiredPermission="org.manage"><ManageFeedback /></Guard>} />
          <Route path="feedback/insights" element={<Guard requiredPermission="org.manage"><ProductInsights /></Guard>} />

          {/* Reports */}
          <Route path="reports" element={<Guard requiredPermission="reports.view"><Reports /></Guard>} />

          {/* Fusion Extracts - admin only */}
          <Route
            path="fusion-extracts"
            element={
              <Guard requiredPermission="platform.admin">
                <FusionExtracts />
              </Guard>
            }
          />

          {/* Reconciliation */}
          <Route path="reconciliation" element={<Guard requiredPermission="runs.use"><Reconciliation /></Guard>} />

          {/* Scheduled Runs */}
          <Route path="scheduled-runs" element={<Guard requiredPermission="runs.use"><ScheduledRuns /></Guard>} />

          {/* Rhino */}
          <Route path="rhino" element={<Guard requiredPermission="rhino.use"><Rhino /></Guard>} />

          {/* Orion: the Slack bridges' assistant inside the product; a workbench like Rhino */}
          <Route path="orion" element={<ChatPage />} />
          <Route path="orion/new" element={<ChatPage />} />
          <Route path="orion/applications" element={<OrionApplications />} />
          <Route path="orion/approvals" element={<OrionApprovals />} />
          <Route path="orion/audit" element={<OrionAudit />} />
          <Route path="orion/patches" element={<OrionPatches />} />
          <Route path="orion/operations" element={<OrionOperations />} />
          <Route path="orion/:threadId" element={<ChatPage />} />
          <Route path="chat" element={<Navigate to="/orion" replace />} />
          <Route path="chat/:threadId" element={<Navigate to="/orion" replace />} />

          {/* Unauthorised */}
          <Route
            path="unauthorized"
            element={
              <Unauthorized />
            }
          />

          {/* Catch-all: say so, rather than redirecting to a real page that
              the reader did not ask for and cannot tell apart from a bug. */}
          <Route path="*" element={<NotFound home="/" />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
